/**
 * War Look shaders (PixiJS v8 filter conventions: `in`/`out`, `finalColor`, `texture()`).
 * Pixi rewrites these for WebGL1 automatically; everything here sticks to GLSL ES 1.0 features
 * (no loops, integer maths or texelFetch).
 *
 * Every shader derives the world position of a pixel from the camera:
 *   world = uCamOrigin + vScreen * uUnitsPerPx      (game units)
 * so ground, fog and gas stay anchored to the map while the camera moves.
 */

/** default filter vertex shader plus `vScreen`: the pixel position in screen (CSS) pixels */
export const WAR_VERTEX = /* glsl */`
in vec2 aPosition;
out vec2 vTextureCoord;
out vec2 vScreen;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

vec4 filterVertexPosition(void) {
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
    return vec4(position, 0.0, 1.0);
}

void main(void) {
    gl_Position = filterVertexPosition();
    vTextureCoord = aPosition * (uOutputFrame.zw * uInputSize.zw);
    vScreen = aPosition * uOutputFrame.zw + uOutputFrame.xy;
}
`;

const ROTATIONS = /* glsl */`
const mat2 R1 = mat2(0.8, -0.6, 0.6, 0.8);
const mat2 R2 = mat2(0.28, -0.96, 0.96, 0.28);
`;

/**
 * Ground materials. uTerrain holds material weights rasterised from the map:
 *   beach (1,0,0) water (0,1,0) road/trail (0,0,1) river bank (0.5,0,0.5) mud = black.
 * uTerrainWide is the same at a quarter of the resolution, whose soft edges give wide wet shorelines.
 *
 * 0.98.7 (the owner: "New ground textures"): the materials are painted tiles (tools/ground/paint_ground.py, four in
 * one atlas with their normal maps: the field's grass, the churned mud, the beach's sand, the tracks' gravel), each
 * laid over the ground at two sizes (the second turned, so its repeat never lines up with the first's) and blended
 * where the terrain says; the water, the ice and the snow stay painted here. At High the materials' normals are lit
 * by the sun (its direction and height from the lighting) with a wet sheen, and the rain pools in the hollows (the
 * normal map's alpha is the height). `groundFragment(true)` is the same ground's normal pass, for the lighting's
 * picture of the shapes (managers/normalLook.ts).
 */
export function groundFragment(normalPass: boolean, withNormals = true): string {
    const normals = normalPass || withNormals;
    // (0.98.7's audit: highp first, Pixi's mediump otherwise: a phone's half precision rounds the world's units, a
    // thousand and more across the map, to a unit and a tile has 40 to 90 of its pixels to one)
    return /* glsl */`precision highp float;
in vec2 vTextureCoord;
in vec2 vScreen;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform sampler2D uNoise;
uniform sampler2D uTerrain;
uniform sampler2D uTerrainWide;
uniform sampler2D uZone;
uniform sampler2D uAlbedo;
uniform sampler2D uNormalMap;
uniform vec2 uMapSize;
uniform vec2 uCamOrigin;
uniform float uUnitsPerPx;
uniform float uTime;
uniform float uRelief;
uniform float uRain;
uniform float uSnow;
// (0.98.6) the sun: the way toward the light on screen (the shadows fall the other way), how strongly it models the
// relief, and its colour on the lit side (managers/lighting.ts); (0.98.7) how long a shadow is for each unit of height
// (the sun's height: the light's slope), and whether the painted materials are loaded (0: flat colours)
uniform vec2 uSunDir;
uniform float uSunLight;
uniform vec3 uLightColor;
uniform float uSunStretch;
uniform float uMaterials;
${ROTATIONS}
const vec3 FIELD_FLAT = vec3(0.320, 0.380, 0.205);
const vec3 MUD_FLAT   = vec3(0.330, 0.260, 0.165);
const vec3 SAND_FLAT  = vec3(0.370, 0.350, 0.280);
const vec3 TRACK_FLAT = vec3(0.360, 0.310, 0.250);
const vec3 WET    = vec3(0.250, 0.205, 0.140);
const vec3 WATER1 = vec3(0.155, 0.195, 0.160);
const vec3 WATER2 = vec3(0.235, 0.280, 0.230);
const vec3 WATERH = vec3(0.500, 0.550, 0.470);
const vec3 BANK   = vec3(0.200, 0.170, 0.120);
const mat2 TURN = mat2(0.0, 1.0, -1.0, 0.0);

// a material's tile (its corner in the atlas, 0 or 1 each way) at a point of the world, one tile every \`units\`
vec2 tileUV(vec2 w, float units, vec2 corner) {
    vec2 f = fract(w / units);
    return (corner * 1024.0 + 8.0 + f * 1008.0) / 2048.0;
}
// the tile laid twice, the second turned and larger, blended by \`m\`: no repeat shows
vec4 laid(sampler2D s, vec2 w, float units, vec2 corner, float m) {
    vec4 one = texture(s, tileUV(w, units, corner));
    vec4 two = texture(s, tileUV(TURN * w + vec2(37.0, 11.0), units * 1.37, corner));
    return mix(one, two, m);
}

void main(void) {
    vec2 w = uCamOrigin + vScreen * uUnitsPerPx;

    // organic material edges: warp the lookup a little
    vec4 n0 = texture(uNoise, w * 0.06);
    vec2 uv = (w + (n0.ra - 0.5) * 2.2) / uMapSize;
    vec4 t = texture(uTerrain, uv);
    vec4 tw = texture(uTerrainWide, w / uMapSize);
    float inside = step(0.0, uv.x) * step(uv.x, 1.0) * step(0.0, uv.y) * step(uv.y, 1.0);
    t = mix(vec4(0.0, 1.0, 0.0, 1.0), t, inside);
    tw = mix(vec4(0.0, 1.0, 0.0, 1.0), tw, inside);

    float bank = clamp(min(t.r, t.b) * 2.0, 0.0, 1.0);
    float beachW = max(t.r - bank * 0.5, 0.0);
    float trailW = max(t.b - bank * 0.5, 0.0);
    float waterW = t.g;
    float mudW = clamp(1.0 - beachW - trailW - waterW - bank, 0.0, 1.0);

    vec4 a = texture(uNoise, w * 0.011);
    vec4 b = texture(uNoise, R1 * w * 0.037 + 0.37);
    vec4 c = texture(uNoise, R2 * w * 0.058 + 0.71);
    vec4 d = texture(uNoise, w * 0.083 + 0.29);
    vec4 e = texture(uNoise, R1 * w * 0.19 + 0.13);

    float shoreWet = smoothstep(0.03, 0.4, tw.g);
    float wetZone = smoothstep(0.55, 0.8, a.a * 0.7 + b.a * 0.3);
    float wet = clamp(max(wetZone * 0.85, shoreWet) + uRain * 0.3, 0.0, 1.0);

    // ---- most of the map is war-worn grassland; full mud only in the shelled regions
    // (uZone is mudZone() from warNoise.ts, baked per map, so decals crowd exactly where the mud is)
    float zone = texture(uZone, (w + (n0.ra - 0.5) * 3.0) / uMapSize).r;
    float bare = smoothstep(0.64, 0.9, a.a * 0.45 + c.g * 0.55);
    float mudShare = clamp(max(zone, max(bare * 0.65, shoreWet * 0.85)), 0.0, 1.0);
    float fieldW = mudW * (1.0 - mudShare);
    float churnW = mudW * mudShare + bank;
    float total = max(fieldW + churnW + beachW + waterW + trailW, 0.001);
    // the two layings' blend: slow patches over the map
    float m = smoothstep(0.3, 0.7, a.g * 0.6 + b.g * 0.4);

    // ---- the materials: colour (their occlusion in the alpha), and their normals and heights (High)
    vec3 col = vec3(0.0);
    vec3 nrm = vec3(0.0);
    float height = 0.5;
    float occl = 1.0;
    if (uMaterials > 0.5) {
        vec4 field = vec4(0.0); vec4 mud = vec4(0.0); vec4 sand = vec4(0.0); vec4 gravel = vec4(0.0);
        vec4 fieldN = vec4(0.5, 0.5, 1.0, 0.5); vec4 mudN = fieldN; vec4 sandN = fieldN; vec4 gravelN = fieldN;
        if (fieldW > 0.002) { field = laid(uAlbedo, w, 21.0, vec2(0.0, 0.0), m); ${normals ? "fieldN = laid(uNormalMap, w, 21.0, vec2(0.0, 0.0), m);" : ""} }
        if (churnW > 0.002) { mud = laid(uAlbedo, w, 25.0, vec2(1.0, 0.0), m); ${normals ? "mudN = laid(uNormalMap, w, 25.0, vec2(1.0, 0.0), m);" : ""} }
        if (beachW > 0.002) { sand = laid(uAlbedo, w, 19.0, vec2(0.0, 1.0), m); ${normals ? "sandN = laid(uNormalMap, w, 19.0, vec2(0.0, 1.0), m);" : ""} }
        if (trailW > 0.002) { gravel = laid(uAlbedo, w, 11.0, vec2(1.0, 1.0), m); ${normals ? "gravelN = laid(uNormalMap, w, 11.0, vec2(1.0, 1.0), m);" : ""} }
        // the field's colour varies over the map: drier and darker patches
        field.rgb *= 0.86 + 0.26 * (d.b * 0.55 + e.b * 0.45);
        field.rgb = mix(field.rgb, field.rgb * vec3(1.18, 1.08, 0.82), smoothstep(0.55, 0.82, b.a) * 0.5);
        mud.rgb *= 0.88 + 0.22 * (d.b * 0.6 + e.b * 0.4);
        mud.rgb = mix(mud.rgb, WET, wet * 0.6);
        vec3 bankC = mix(mud.rgb, BANK * (0.88 + 0.24 * b.r), 0.7);
        sand.rgb = mix(sand.rgb, WET * 1.25, max(shoreWet * 0.8, smoothstep(0.58, 0.8, b.a) * 0.55));
        gravel.rgb = mix(gravel.rgb, WET * 0.9, smoothstep(0.6, 0.8, b.a) * 0.6);
        float churnOnly = max(churnW - bank, 0.0);
        col = (field.rgb * fieldW + mud.rgb * churnOnly + bankC * bank + sand.rgb * beachW + gravel.rgb * trailW) / max(total - waterW, 0.001);
        occl = (field.a * fieldW + mud.a * churnW + sand.a * beachW + gravel.a * trailW) / max(total - waterW, 0.001);
        vec4 nAll = (fieldN * fieldW + mudN * churnW + sandN * beachW + gravelN * trailW) / max(total - waterW, 0.001);
        nrm = normalize(nAll.xyz * 2.0 - 1.0);
        height = nAll.a;
    } else {
        col = (FIELD_FLAT * fieldW + MUD_FLAT * churnW + SAND_FLAT * beachW + TRACK_FLAT * trailW) / max(total - waterW, 0.001);
        col *= 0.88 + 0.22 * (d.b * 0.6 + e.b * 0.4);
        nrm = vec3(0.0, 0.0, 1.0);
    }

    // ---- the rain pools in the hollows: the sky's grey on the water, the ground flat under it
    float pool = uRain * (1.0 - smoothstep(0.14, 0.34, height)) * (1.0 - waterW) * (fieldW + churnW + trailW) / total;
    vec3 skyC = uLightColor * 0.42 + vec3(0.08, 0.1, 0.12);
    col = mix(col, skyC, pool * 0.8);
    nrm = normalize(mix(nrm, vec3(0.0, 0.0, 1.0), pool));

    // ---- murky water, slowly moving
    float r1 = texture(uNoise, w * 0.05 + vec2(uTime * 0.012, uTime * 0.008)).g;
    float r2 = texture(uNoise, R2 * w * 0.075 + vec2(-uTime * 0.01, uTime * 0.011)).g;
    float depth = smoothstep(0.4, 1.0, tw.g);
    vec3 water = mix(WATER2, WATER1, depth);
    water *= 0.9 + 0.14 * b.r;
    water = mix(water, WATERH, smoothstep(0.8, 0.92, r1 * 0.55 + r2 * 0.45) * (0.3 + 0.25 * uRain));
    water = mix(water, vec3(0.33, 0.30, 0.21), (1.0 - depth) * smoothstep(0.55, 0.8, c.r) * 0.45);
    float waterShare = waterW / total;
    col = mix(col, water, waterShare);
    // (the water's own ripples as a faint normal)
    vec3 waterN = normalize(vec3((r1 - 0.5) * 0.35, (r2 - 0.5) * 0.35, 1.0));
    nrm = normalize(mix(nrm, waterN, waterShare));

    // ---- snow (1.05): the land and the silt under a white blanket, brighter in its drifts, the shelled ground's holes
    // showing through grey; the tracks trodden to slush; the water frozen (0.98.5): a pale sheet of ice, bluer and
    // darker over the deep water, frost blooming across it, cracks running through it, a glint here and there
    if (uSnow > 0.0) {
        float drift = smoothstep(0.25, 0.85, a.r * 0.55 + b.g * 0.45);
        vec3 snowC = mix(vec3(0.80, 0.83, 0.87), vec3(0.94, 0.95, 0.97), drift) * (0.95 + 0.06 * d.b);
        snowC = mix(snowC, vec3(0.62, 0.64, 0.68), zone * smoothstep(0.6, 0.92, c.g) * 0.55);
        vec3 slush = mix(vec3(0.50, 0.50, 0.52), snowC, 0.3 + 0.4 * smoothstep(0.35, 0.8, c.r));
        float iceDepth = smoothstep(0.12, 0.85, tw.g);
        vec3 ice = mix(vec3(0.74, 0.81, 0.88), vec3(0.56, 0.68, 0.80), iceDepth);
        ice *= 0.93 + 0.12 * a.r;
        float frost = smoothstep(0.5, 0.86, a.r * 0.5 + d.b * 0.5);
        float frostFine = smoothstep(0.62, 0.92, e.b * 0.5 + d.b * 0.5) * 0.45;
        ice = mix(ice, vec3(0.95, 0.96, 0.98), max(frost * 0.75, frostFine));
        float rBig = texture(uNoise, w * 0.012 + 0.61).r;
        float rMid = texture(uNoise, R1 * w * 0.031 + 0.37).r;
        float rBigO = texture(uNoise, (w + vec2(0.7, 0.55)) * 0.012 + 0.61).r;
        float rMidO = texture(uNoise, R1 * (w + vec2(0.45, 0.35)) * 0.031 + 0.37).r;
        float crack = max(1.0 - smoothstep(0.004, 0.016, abs(rBig - 0.5)), (1.0 - smoothstep(0.003, 0.012, abs(rMid - 0.46))) * 0.75);
        float litEdge = max(1.0 - smoothstep(0.004, 0.016, abs(rBigO - 0.5)), (1.0 - smoothstep(0.003, 0.012, abs(rMidO - 0.46))) * 0.75) * (1.0 - crack);
        ice = mix(ice, vec3(0.97, 0.98, 1.0), litEdge * 0.7);
        ice = mix(ice, vec3(0.14, 0.22, 0.34), crack * 0.8);
        ice += vec3(0.15) * smoothstep(0.84, 0.96, e.b);
        ice = mix(mix(snowC, ice, 0.84), ice, smoothstep(0.04, 0.5, tw.g));
        vec3 snowCol = (snowC * (fieldW + churnW + beachW) + slush * trailW + ice * waterW) / total;
        col = mix(col, snowCol, uSnow);
        // (the snow lies soft over the ground's shape: its slopes gentled, the drifts' own)
        vec3 driftN = normalize(vec3((a.r - 0.5) * 0.3, (b.g - 0.5) * 0.3, 1.0));
        nrm = normalize(mix(nrm, normalize(mix(nrm, driftN, 0.75)), uSnow));
    }
${normalPass ? `
    finalColor = vec4(nrm * 0.5 + 0.5, 1.0);
` : `
    col *= mix(1.0, occl, 0.55);
    // relief: lit from the sun's side (0.98.6: the way the lighting has it, low at dusk, the lightning's for a flash),
    // plus a wet sheen in the sun's colour that brightens in the rain (0.98.7: by the materials' own normals)
    if (uRelief > 0.5) {
        vec3 l = normalize(vec3(uSunDir * uSunStretch, 1.0));
        float lit = (dot(nrm, l) - l.z) * uSunLight;
        float solid = 1.0 - waterShare;
        col *= 1.0 + lit * 0.9 * solid;
        vec3 h = normalize(l + vec3(0.0, 0.0, 1.0));
        float spec = pow(max(dot(nrm, h), 0.0), 26.0);
        // (0.98.7's lab: seen from above under the rain's grey every flat wet top took the whole sheen, the stones and
        // the pools gone white; in the rain a soft sheen, the pools silver, the clear sky's wet as it was)
        col += uLightColor * spec * (0.3 * wet * solid * (1.0 - 0.6 * uRain) + 0.22 * pool + 0.2 * waterShare) * (0.5 + 0.5 * uRain);
    }
    finalColor = vec4(col, 1.0);
`}
}
`;
}

/** the ground at High (its normals lit by the sun), at Medium (its colours alone), and its normal pass */
export const GROUND_FRAGMENT = groundFragment(false, true);
export const GROUND_FRAGMENT_MEDIUM = groundFragment(false, false);
export const GROUND_NORMAL_FRAGMENT = groundFragment(true);

/** fog banks + the mustard-gas cloud + the next-zone ring, drawn at reduced resolution over the world */
export const FX_FRAGMENT = /* glsl */`
in vec2 vTextureCoord;
in vec2 vScreen;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform sampler2D uNoise;
uniform vec2 uCamOrigin;
uniform float uUnitsPerPx;
uniform float uTime;
uniform float uFog;
uniform vec3 uFogTint;
uniform vec2 uWind;
uniform vec2 uPlayer;
uniform vec2 uGasCenter;
uniform float uGasRadius;
uniform float uGasOn;
uniform float uAdvancing;
// (0.98.4) what the zone is: 0 the gas, 1 dusk's dust storm, 2 the night's wildfire (its cloud in their colours)
uniform float uGasLook;
uniform vec2 uNextCenter;
uniform float uNextRadius;
uniform float uNextOn;
uniform float uPxSize;
// (0.98.6) the sun: the way toward the light on screen, how much the fog glows toward it, its colour, the screen's size
uniform vec2 uSunDir;
uniform float uSunGlow;
uniform vec3 uLightColor;
uniform vec2 uScreen;
${ROTATIONS}
void main(void) {
    vec2 w = uCamOrigin + vScreen * uUnitsPerPx;
    vec4 acc = vec4(0.0);

    // ---- low fog and drifting smoke banks, anchored to the ground
    if (uFog > 0.0) {
        vec4 fa = texture(uNoise, w * 0.019 + uWind * uTime * 0.019);
        vec4 fb = texture(uNoise, R1 * (w * 0.043 - uWind * uTime * 0.027) + fa.ra * 0.4);
        float n = fa.r * 0.55 + fb.r * 0.45;
        float banks = smoothstep(0.58, 0.84, texture(uNoise, w * 0.0042 + uWind * uTime * 0.0036).a);
        float fog = (smoothstep(0.52, 0.9, n) * 0.55 + banks * 0.65) * uFog;
        fog *= 0.3 + 0.7 * smoothstep(3.0, 16.0, distance(w, uPlayer));
        fog = clamp(fog, 0.0, 0.7);
        // (the sky's own haze: white in the snow, warm at dusk, dark in a storm: 1.05)
        vec3 fogC = clamp(mix(vec3(0.36, 0.36, 0.32), vec3(0.47, 0.47, 0.42), fb.g) * uFogTint, 0.0, 1.0);
        // (0.98.6: lit toward the sun, in its colour: at dusk the haze glows toward the low sun's corner)
        vec2 mid = vScreen - uScreen * 0.5;
        float toward = 0.5 + 0.5 * dot(mid / max(length(mid), 1.0), uSunDir);
        fogC = mix(fogC, clamp(fogC * uLightColor * 1.35, 0.0, 1.0), uSunGlow * toward);
        acc = vec4(fogC * fog, fog);
    }

    // ---- mustard gas: a low rolling wall that tumbles inward from the edge of the safe zone
    if (uGasOn > 0.5) {
        vec2 toC = w - uGasCenter;
        float dist = length(toC);
        vec2 nrm = toC / max(dist, 0.001);
        float d = dist - uGasRadius;
        float speed = mix(0.5, 1.7, uAdvancing);
        vec2 roll = nrm * uTime * speed;
        vec2 drift = vec2(-nrm.y, nrm.x) * uTime * 0.25;
        vec2 q = (w + roll + drift) * 0.045;
        vec4 g1 = texture(uNoise, q);
        vec4 g2 = texture(uNoise, R2 * (w + roll * 1.6) * 0.07 + g1.ra * 0.55);
        float billow = g1.r * 0.72 + g2.r * 0.28;
        // the same billows sampled a little toward the light (top-left): their difference shades the cloud
        vec2 wl = w + vec2(-1.1, -1.1);
        vec4 l1 = texture(uNoise, (wl + roll + drift) * 0.045);
        float lit = clamp((g1.r - l1.r) * 3.0, -1.0, 1.0);

        float edge = d + (billow - 0.5) * 12.0;
        float wall = smoothstep(-2.0, 10.0, edge);
        // thin tendrils creeping ahead of the front
        float wisps = smoothstep(0.58, 0.82, g2.g) * smoothstep(-7.0, 0.0, edge) * (1.0 - smoothstep(0.0, 5.0, edge)) * 0.3;
        float deep = smoothstep(35.0, 120.0, d);
        float dens = max(wall * mix(0.66, 0.52, deep) * (0.8 + 0.28 * g2.g), wisps);

        vec3 core = vec3(0.30, 0.285, 0.13);
        vec3 body = vec3(0.50, 0.475, 0.255);
        vec3 chlorine = vec3(0.47, 0.525, 0.28);
        vec3 rimC = vec3(0.68, 0.645, 0.42);
        vec3 deepC = vec3(0.58, 0.585, 0.5);
        if (uGasLook > 1.5) {
            // the wildfire's smoke (0.98.4): black and grey, lit orange along its front
            core = vec3(0.07, 0.05, 0.04); body = vec3(0.24, 0.21, 0.19); chlorine = vec3(0.34, 0.31, 0.29);
            rimC = vec3(0.95, 0.5, 0.18); deepC = vec3(0.18, 0.16, 0.15);
        } else if (uGasLook > 0.5) {
            // the dust storm (0.98.4): tan, paler where the light catches it
            core = vec3(0.52, 0.40, 0.22); body = vec3(0.76, 0.61, 0.36); chlorine = vec3(0.84, 0.70, 0.46);
            rimC = vec3(0.92, 0.80, 0.56); deepC = vec3(0.72, 0.60, 0.42);
        }
        vec3 gc = mix(core, body, smoothstep(0.28, 0.78, billow));
        gc = mix(gc, chlorine, smoothstep(0.55, 0.85, texture(uNoise, w * 0.0075 + uTime * 0.004).a) * 0.4);
        float rim = smoothstep(-2.0, 1.5, edge) * (1.0 - smoothstep(1.5, 6.0, edge));
        gc = mix(gc, rimC, rim * 0.4);
        gc = mix(gc, deepC, deep * 0.45);
        gc *= 0.84 + 0.18 * lit + 0.12 * billow;
        float ga = clamp(dens, 0.0, 0.78);
        acc = vec4(gc * ga, ga) + acc * (1.0 - ga);
    }

    // ---- next safe zone: a thin dashed khaki ring with a soft glow
    // (this layer is drawn smaller than the screen: the line is kept at least about a pixel of it wide)
    if (uNextOn > 0.5) {
        float px = abs(distance(w, uNextCenter) - uNextRadius) / uUnitsPerPx;
        float ang = atan(w.y - uNextCenter.y, w.x - uNextCenter.x);
        float dash = step(0.45, fract(ang * uNextRadius / 5.0));
        float k = max(1.0, uPxSize * 0.6);
        float la = clamp((1.0 - smoothstep(1.0 * k, 2.2 * k, px)) * dash * 0.7 + (1.0 - smoothstep(1.5, 9.0, px)) * 0.18, 0.0, 0.8);
        acc = vec4(vec3(0.86, 0.81, 0.56) * la, la) + acc * (1.0 - la);
    }
    finalColor = acc;
}
`;

/**
 * final grade over the whole world: grade, vignette, gas mask, in-gas tint, artillery flashes; 0.98.6 (the lighting at
 * High, war/warBloom.ts): the bloom, a blurred picture of the frame's bright parts added back over it, and the heat
 * haze, the picture wavering where the heat map says a fire burns; 0.98.7: the shapes' relief, the sun and the lights
 * on every slope of the normals' picture (managers/normalLook.ts, managers/lightFans.ts)
 */
export const GRADE_FRAGMENT = /* glsl */`
in vec2 vTextureCoord;
in vec2 vScreen;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform sampler2D uBloom;
uniform sampler2D uHeat;
uniform vec4 uInputPixel;
uniform vec4 uInputClamp;
uniform vec2 uScreen;
uniform float uTime;
uniform float uSaturation;
uniform float uContrast;
uniform float uGamma;
uniform float uLift;
uniform vec3 uShadowTint;
uniform vec3 uMidTint;
uniform vec3 uHighTint;
uniform float uVignette;
uniform float uMask;
uniform float uGasTint;
// (0.98.4) what the zone is: 0 the gas, 1 dusk's dust storm, 2 the night's wildfire (the world's cast inside it)
uniform float uGasLook;
uniform float uFlash;
uniform vec2 uFlashDir;
uniform vec3 uLightColor;
uniform float uBloomOn;
uniform float uBloomStrength;
uniform vec2 uBloomScale;
uniform float uHeatOn;
uniform float uHeatWobble;
// (0.98.7) the shapes' relief: the normals' picture, the lights' relief (red lit, green shaded), the sun's slope,
// how boldly it models the shapes, and whether any of it is on
uniform sampler2D uNormal;
uniform sampler2D uRelief;
uniform vec3 uSunL;
uniform float uSunRelief;
uniform float uReliefOn;

float luma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
}

void main(void) {
    vec2 tex = vTextureCoord;
    // the heat haze: over a fire the air shimmers, the picture pulled a few pixels this way and that, rising
    if (uHeatOn > 0.5) {
        float heat = texture(uHeat, vScreen / uScreen).r;
        if (heat > 0.004) {
            float wx = sin(vScreen.y * 0.13 - uTime * 11.0 + vScreen.x * 0.05) * 0.7 + sin(vScreen.x * 0.21 + uTime * 8.0) * 0.3;
            float wy = sin(vScreen.x * 0.11 + uTime * 9.0 + vScreen.y * 0.04);
            // (screen pixels to the picture's own: its pixels per screen pixel, then to its texture's measure)
            vec2 shift = vec2(wx, wy) * heat * uHeatWobble * (uInputPixel.x / uScreen.x) * uInputPixel.zw;
            tex = clamp(tex + shift, uInputClamp.xy, uInputClamp.zw);
        }
    }
    vec4 src = texture(uTexture, tex);
    vec3 c = src.a > 0.0 ? src.rgb / src.a : src.rgb;
    // the shapes' relief (0.98.7): the sun on each slope (toward it brighter, away from it darker, flat as it was),
    // and the lights' own (a flash beside a crate lights its near side)
    if (uReliefOn > 0.5) {
        vec2 suv = vScreen / uScreen;
        vec3 n = texture(uNormal, suv).rgb * 2.0 - 1.0;
        float sunLit = (dot(n, uSunL) - uSunL.z) * uSunRelief;
        vec2 lights = texture(uRelief, suv).rg;
        float lit = clamp(1.0 + sunLit + lights.r - lights.g, 0.2, 2.2);
        c *= lit * mix(vec3(1.0), uLightColor, clamp(sunLit, 0.0, 1.0) * 0.6);
    }
    // the bloom: the frame's bright parts, blurred wide and added back (made in warBloom.ts before this pass)
    if (uBloomOn > 0.5) c += texture(uBloom, vTextureCoord * uBloomScale).rgb * uBloomStrength;
    float l = luma(c);

    // desaturate, but spare vivid reds (blood, hits); foliage turns olive
    float redness = clamp((c.r - max(c.g, c.b) - 0.25) * 3.0, 0.0, 1.0) * step(0.35, c.r);
    float greenness = clamp((c.g - max(c.r, c.b)) * 3.5, 0.0, 1.0);
    c = mix(vec3(l), c, mix(uSaturation, 1.0, redness * 0.55));
    c = mix(c, vec3(l * 1.06 + 0.012, l * 1.0 + 0.006, l * 0.72), greenness * 0.32);

    // split tone: olive shadows, green-yellow mids, warm khaki highlights
    float hl = smoothstep(0.55, 1.0, l);
    float sh = 1.0 - smoothstep(0.0, 0.42, l);
    c *= mix(uMidTint, uHighTint, hl);
    c = mix(c, c * (uShadowTint * 6.0), sh * 0.2);

    // contrast, gamma, lifted and tinted blacks
    c = (c - 0.45) * uContrast + 0.45;
    c = pow(max(c, 0.0), vec3(uGamma));
    c = c * (1.0 - uLift) + uLift * uShadowTint * 3.0;

    // inside the gas the world turns sickly yellow; inside the dust, tan; inside the fire, hot and orange
    vec3 inside = uGasLook > 1.5 ? c * vec3(1.12, 0.82, 0.6) + vec3(0.07, 0.02, 0.0)
        : uGasLook > 0.5 ? c * vec3(1.08, 0.95, 0.72) + vec3(0.06, 0.04, 0.0)
        : c * vec3(1.05, 1.02, 0.7) + vec3(0.035, 0.03, 0.0);
    c = mix(c, inside, uGasTint);

    vec2 uv = vScreen / uScreen;
    vec2 q = (uv - 0.5) * vec2(uScreen.x / uScreen.y, 1.0);

    // distant artillery: a brief warm glow from one side of the horizon
    float side = clamp(dot(normalize(q + 0.0001), uFlashDir) * 0.5 + 0.5, 0.0, 1.0);
    c += vec3(0.95, 0.78, 0.52) * uFlash * (0.2 + 0.8 * side * smoothstep(0.1, 0.9, length(q)));

    // vignette, and the gas mask: two lenses with fogged, condensing edges
    float vig = smoothstep(0.4, 1.08, length(q));
    vec2 eL = (q - vec2(-0.3, -0.02)) * vec2(1.0, 1.08);
    vec2 eR = (q - vec2(0.3, -0.02)) * vec2(1.0, 1.08);
    float lens = min(length(eL), length(eR));
    float mask = smoothstep(0.4, 0.5, lens);
    float dark = max(vig * uVignette, mask * uMask * 0.76);
    c = mix(c, vec3(0.07, 0.064, 0.032), dark);
    float rim = 1.0 - smoothstep(0.0, 0.025, abs(lens - 0.44));
    c += vec3(0.3, 0.29, 0.22) * rim * uMask * 0.22;
    float cond = smoothstep(0.24, 0.43, lens) * (1.0 - mask) * uMask;
    float speck = hash12(floor(vScreen / 3.0) + 7.0);
    c = mix(c, vec3(0.64, 0.64, 0.57), cond * (0.08 + 0.14 * speck));

    c = clamp(c, 0.0, 1.0);
    finalColor = vec4(c * src.a, src.a);
}
`;
